<template>
  <div>
    <h1 class="text-2xl font-medium">
      {{ $t("dashboard.operation.summary") }}
    </h1>
    <div
      class="h-40 flex flex-row pt-2 space-x-4 items-center justify-center"
      v-if="errorMessage != null"
    >
      <p class="text-error">{{ errorMessage }}</p>
      <app-button-outline
        class="h-10 space-x-2 w-auto justify-between"
        @click="getSummary"
      >
        <div
          class="
            place-items-start
            flex flex-col
            text-gray-700
            font-semibold
            antialiased
            text-xs
          "
        >
          <span class="text-gray-700">
            {{ $t("general.try_again") }}
          </span>
        </div>
      </app-button-outline>
    </div>
    <div
      class="
        py-3
        flex flex-col
        justify-between
        md:flex-row md:space-y-0 md:space-x-3
        space-y-3 space-x-0
      "
      v-if="errorMessage == null"
    >
      <p class="border p-2 rounded-md">
        {{ $moment(select_date?.from).format("DD-MM-YYYY") }}
        {{ $t("general.to") }}
        {{ $moment(select_date?.to).format("DD-MM-YYYY") }}
      </p>
      <app-button-select-date
        @select_date="select_date = $event"
        @select_date_format="select_date_format = $event"
        @on_select="getSummary"
      />
    </div>
    <div
      class="h-40 flex flex-row pt-4 space-x-4 items-center justify-center"
      v-if="errorMessage != null"
    >
      <p class="text-error">{{ errorMessage }}</p>
      <app-button-outline
        class="h-10 space-x-2 w-auto justify-between"
        @click="select_transaction_summary"
      >
        <div
          class="
            place-items-start
            flex flex-col
            text-gray-700
            font-semibold
            antialiased
            text-xs
          "
        >
          <span class="text-gray-700">
            {{ $t("general.try_again") }}
          </span>
        </div>
      </app-button-outline>
    </div>
    <div
      class="grid grid-rows-1 md:grid-cols-3 gap-3 mt-3 text-gray-600"
      v-if="errorMessage == null"
    >
      <app-card-box class="space-y-1">
        <p class="text-lg font-medium">
          {{ $t("dashboard.operation.business_registered") }}
        </p>
        <paragraph-shimmer
          :is-loading="loading"
          :lines="1"
          class="w-10"
          :random-size="true"
        />
        <p class="text-2xl" v-if="!loading">
          {{ summary.total_business_register }}
        </p>
      </app-card-box>
      <app-card-box class="space-y-1">
        <p class="text-lg font-medium">
          {{ $t("dashboard.operation.business_created") }}
        </p>
        <paragraph-shimmer
          :is-loading="loading"
          :lines="1"
          class="w-10"
          :random-size="true"
        />
        <p class="text-2xl" v-if="!loading">
          {{ summary.total_business_created }}
        </p>
      </app-card-box>
      <app-card-box class="space-y-1">
        <p class="text-lg font-medium">
          {{ $t("dashboard.operation.business_pending") }}
        </p>
        <paragraph-shimmer
          :is-loading="loading"
          :lines="1"
          class="w-10"
          :random-size="true"
        />
        <p class="text-2xl font-light" v-if="!loading">
          {{ summary.total_business_pending }}
        </p>
      </app-card-box>
      <app-card-box class="space-y-1">
        <p class="text-lg font-medium">
          {{ $t("dashboard.operation.business_approved") }}
        </p>
        <paragraph-shimmer
          :is-loading="loading"
          :lines="1"
          class="w-10"
          :random-size="true"
        />
        <p class="text-2xl font-light" v-if="!loading">
          {{ summary.total_business_approved }}
        </p>
      </app-card-box>
      <app-card-box class="space-y-1">
        <p class="text-lg font-medium">
          {{ $t("dashboard.operation.business_amended") }}
        </p>
        <paragraph-shimmer
          :is-loading="loading"
          :lines="1"
          class="w-10"
          :random-size="true"
        />
        <p class="text-2xl font-light" v-if="!loading">
          {{ summary.total_business_amended }}
        </p>
      </app-card-box>
      <app-card-box class="space-y-1">
        <p class="text-lg font-medium">
          {{ $t("dashboard.operation.business_rejected") }}
        </p>
        <paragraph-shimmer
          :is-loading="loading"
          :lines="1"
          class="w-10"
          :random-size="true"
        />
        <p class="text-2xl font-light" v-if="!loading">
          {{ summary.total_business_rejected }}
        </p>
      </app-card-box>
    </div>
    <div v-if="errorMessage == null">
      <app-card-box class="space-y-1 mt-5">
        <p class="text-xl font-medium">
          {{ $t("dashboard.operation.payment_gateway") }}
        </p>
        <app-tab
          :tabs="paymentGatewayTabs"
          @tab-changed="select_payment_gateway = $event"
        >
          <div
            v-for="(payment_method, key) in getPaymentGatewayTabs(
              select_payment_gateway
            )"
            :key="key"
            class="grid grid-rows-1 md:grid-cols-3 gap-3 mt-3 text-gray-600"
          >
            <app-card-box class="space-y-1">
              <p class="text-lg font-medium">
                {{ $t("dashboard.account.online_b2c") }}
              </p>
              <paragraph-shimmer
                :is-loading="loading"
                :lines="1"
                class="w-10"
                :random-size="true"
              />
              <p class="text-2xl" v-if="!loading">
                {{ payment_method.online_banking_b2c }}
              </p>
            </app-card-box>
            <app-card-box class="space-y-1">
              <p class="text-lg font-medium">
                {{ $t("dashboard.account.online_b2b") }}
              </p>
              <paragraph-shimmer
                :is-loading="loading"
                :lines="1"
                class="w-10"
                :random-size="true"
              />
              <p class="text-2xl" v-if="!loading">
                {{ payment_method.online_banking_b2b }}
              </p>
            </app-card-box>
            <app-card-box class="space-y-1">
              <p class="text-lg font-medium">
                {{ $t("dashboard.account.credit_card") }}
              </p>
              <paragraph-shimmer
                :is-loading="loading"
                :lines="1"
                class="w-10"
                :random-size="true"
              />
              <p class="text-2xl" v-if="!loading">
                {{ payment_method.credit_debit_card }}
              </p>
            </app-card-box>
          </div></app-tab
        >
      </app-card-box>
    </div>
  </div>
</template>

<script>
import { ParagraphShimmer } from "vue3-shimmer";

export default {
  data() {
    return {
      select_payment_gateway: null,
      select_date_format: null,
      select_date: {},
    };
  },
  components: {
    ParagraphShimmer,
  },
  computed: {
    summary() {
      return this.$store.getters["dashboardStore/operationSummary"];
    },
    loading() {
      return this.$store.getters["dashboardStore/operationSummaryLoading"];
    },
    errorMessage() {
      return this.$store.getters["dashboardStore/operationSummaryMessage"];
    },
    dateFrom() {
      return this.$store.getters["dashboardStore/dateFrom"];
    },
    dateTo() {
      return this.$store.getters["dashboardStore/dateTo"];
    },
    paymentGatewayTabs() {
      var tabs =
        this.summary.total_payment_gateway?.map((data) => {
          var payment_gateway = Object.keys(data)[0];
          return {
            key: payment_gateway,
            title: payment_gateway?.replace("_", " "),
          };
        }) ?? [];

      this.selectTabPaymentGateway(tabs[0] ?? null);
      return tabs;
    },
  },
  methods: {
    getPaymentGatewayTabs(tab) {
      return (
        this.summary.total_payment_gateway?.find((data) => {
          return Object.keys(data)[0] == tab;
        }) ?? {}
      );
    },
    selectTabPaymentGateway(tab) {
      this.select_payment_gateway = tab?.key;
    },
    getSummary() {
      this.$store.dispatch(
        "dashboardStore/retrieveOperationDashboardSummary",
        this.select_date_format
      );
    },
  },
};
</script>
